import React from 'react';
import { Route, Routes } from 'react-router-dom';

import { LineLoader, PermissionFilter } from '@timmons-group/shared-react-components';
import { AUTH_STATES, ACLS } from './constants';
import { useAuth } from '@timmons-group/shared-react-components/hooks';
import EmbeddedPortalApp from './pages/EmbeddedPortalApp';
import Home from './pages/Home';
import Layout from './pages/Layout';
import Recipients from './pages/Recipients';
import RecipientsFormView from './pages/RecipientsFormView';
import OrgRecipientsFormView from './pages/OrgRecipientsFormView';
import Search from './pages/Search';
import ViewUnit from './pages/ViewUnit';
import ViewPractice from './pages/ViewPractice';
import ViewActivity from './pages/ViewActivity';

const AppRoutes = () => {
  // This hook is a useContext for the ProvideAuth contextProvider found in AppWrapper.js
  // It is used to get the auth object and dispatch actions to it
  // The useProvideAuth in useAuth.js contains the exposed properties and methods
  const { authState } = useAuth();

  // Use this to debug the auth state if needed
  // useEffect(() => {
  //   console.log('Auth:', authState, authState.state === AUTH_STATES.LOGGING_IN)
  // }, [authState.state]);

  // This is a modified take on the official react-router-dom Auth example:
  // https://reactrouter.com/docs/en/v6/examples/auth
  return (
    <>
      <Routes>
        <Route path="/" element={<Layout />}>
          {(authState.state === AUTH_STATES.LOGGING_IN || authState.state === AUTH_STATES.INITIALIZING) ?
            // This will put a placeholder route in that matches anything while logging in
            // This way we dont forget the user's current location
            // Once logged in they get the right outlet if they have access to it
            (<>
              <Route index element={
                <Home />
              } />
              <Route path="*" element={
                <LineLoader color="primary" message="Logging you in..." />
              } />
            </>) :
            (<>
              <Route index element={
                <Home />
              } />

              <Route path="explorer" element={
                <EmbeddedPortalApp portalApp="explorer" key="explorer"/>
              } />

              <Route path="dashboard" element={
                <EmbeddedPortalApp portalApp="dashboard" key="dashboard"/>
              } />

              {/* Search routes */}
              <Route path="search">
                <Route path="units" element={
                  <PermissionFilter isRoute={true} permission={ACLS.SEE_SEARCH_PAGE}>
                    <Search initialTabIndex={0}/>
                  </PermissionFilter>
                } />
                <Route path="activities" element={
                  <PermissionFilter isRoute={true} permission={ACLS.SEE_ACTIVITIES}>
                    <Search initialTabIndex={1} />
                  </PermissionFilter>
                } />
                <Route path="planned_activities" element={
                  <PermissionFilter isRoute={true} permission={ACLS.SEE_ACTIVITIES}>
                    <Search initialTabIndex={2} />
                  </PermissionFilter>
                } />
              </Route>

              {/* Units */}
              <Route path="units/:id" element={
                <PermissionFilter isRoute={true} permission={ACLS.SEE_UNITS}>
                  <ViewUnit />
                </PermissionFilter>
              } />

              {/* Practices */}
              <Route path="practices/:id" element={
                <PermissionFilter isRoute={true} permission={ACLS.SEE_PRACTICES}>
                  <ViewPractice />
                </PermissionFilter>
              } />

              {/* Activities */}
              <Route path="activities/:id" element={
                <PermissionFilter isRoute={true} permission={ACLS.SEE_ACTIVITIES}>
                  <ViewActivity />
                </PermissionFilter>
              } />

              {/* Individual Recipients */}
              <Route path="recipients" element={
                <PermissionFilter isRoute={true} permission={ACLS.SEE_RECIP}>
                  <Recipients />
                </PermissionFilter>
              } />
              <Route path="recipients/create" element={
                <PermissionFilter isRoute={true} permission={ACLS.CREATE_RECIP}>
                  <RecipientsFormView />
                </PermissionFilter>
              } />
              <Route path="recipients/editRecipient/:streamID" element={
                <PermissionFilter isRoute={true} permission={ACLS.EDIT_RECIP}>
                  <RecipientsFormView />
                </PermissionFilter>
              } />

              {/* Organization Recipients */}
              <Route path="orgrecipients/create" element={
                <PermissionFilter isRoute={true} permission={ACLS.CREATE_ORG_RECIP}>
                  <OrgRecipientsFormView />
                </PermissionFilter>
              } />
              <Route path="orgrecipients/editRecipient/:streamID" element={
                <PermissionFilter isRoute={true} permission={ACLS.EDIT_ORG_RECIP}>
                  <OrgRecipientsFormView />
                </PermissionFilter>
              } />
            </>)
          }
        </Route>
      </Routes>
    </>
  )
}

export default AppRoutes;
